import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import { CloudArrowUpFilled } from "@fluentui/react-icons";
import { toast } from "components/FluentToast";
import SelectField from "components/controls9/SelectField";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppStore } from "store/AppStore";
import { useListPageStore } from "store/ListPageStore";

export const ImportToGosDialog = () => {
  const [
    { importToGosDialog, importToGosStatus },
    { setListStoreValue, importToGos },
  ] = useListPageStore();

  const [{ gosTaxonomyData }] = useAppStore();

  const { t } = useTranslation();
  const isLoading = importToGosStatus === "loading";
  const offer = importToGosDialog?.offer;
  const isGeneralBusiness = offer?.offerRequestType === "F100056";

  const form = useForm({
    defaultValues: {
      policyType: "",
      costCenterCodeId: "",
      acquisitionAreaCodeId: "",
    },
  });

  const { getValues, setError, clearErrors, reset } = form;

  const policyTypeOptions = React.useMemo(() => {
    if (!offer) return [];
    const opts = [];
    if (offer.policyMtplId) {
      opts.push({ value: "MTPL", label: "MTPL" });
    }
    if (offer.policyCascoId) {
      opts.push({ value: "CASCO", label: "CASCO" });
    }
    return opts;
  }, [offer]);

  const costCenterOptions = React.useMemo(() => {
    const items = gosTaxonomyData?.appInfo?.dataObj?.CostCenter;
    if (!items) return [];
    return items
      .map((item: any) => ({
        value: String(item.id),
        label: item.code,
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [gosTaxonomyData]);

  const acquisitionAreaOptions = React.useMemo(() => {
    const items = gosTaxonomyData?.appInfo?.dataObj?.AcquisitionArea;
    if (!items) return [];
    return items
      .map((item: any) => ({
        value: String(item.id),
        label: item.code,
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
  }, [gosTaxonomyData]);

  React.useEffect(() => {
    if (importToGosDialog.isOpen) {
      reset({
        policyType:
          policyTypeOptions.length === 1 ? policyTypeOptions[0].value : "",
        costCenterCodeId: "",
        acquisitionAreaCodeId: "",
      });
    }
  }, [importToGosDialog.isOpen, reset, policyTypeOptions]);

  const closeDialog = () => {
    setListStoreValue("importToGosDialog", {
      isOpen: false,
      offer: null,
    });
    setListStoreValue("importToGosStatus", "none");
  };

  const handleSubmit = () => {
    clearErrors();
    const values = getValues();
    let hasError = false;

    if (!values.policyType) {
      setError("policyType", {
        message: t("greco.form.errors.required"),
      });
      hasError = true;
    }
    if (isGeneralBusiness && !values.costCenterCodeId) {
      setError("costCenterCodeId", {
        message: t("greco.form.errors.required"),
      });
      hasError = true;
    }
    if (isGeneralBusiness && !values.acquisitionAreaCodeId) {
      setError("acquisitionAreaCodeId", {
        message: t("greco.form.errors.required"),
      });
      hasError = true;
    }

    if (hasError) return;

    const payload = {
      appInfo: {
        dataObj: {
          offerIds: [offer.offerId],
          isMtplImport: values.policyType === "MTPL",
          costCenterCodeId: isGeneralBusiness
            ? Number(values.costCenterCodeId)
            : null,
          acquisitionAreaCodeId: isGeneralBusiness
            ? Number(values.acquisitionAreaCodeId)
            : null,
        },
      },
    };

    importToGos(
      payload,
      () => {
        closeDialog();
        toast.success(t("bcr.importToGos.successMessage"));
      },
      () => {
        closeDialog();
      }
    );
  };

  return (
    <Dialog open={importToGosDialog.isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t("bcr.importToGos.title")}</DialogTitle>
          <DialogContent>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ fontWeight: 600, marginBottom: 4, display: "block" }}>
                  {t("bcr.importToGos.policyType.label")}*
                </label>
                <SelectField
                  isLoading={isLoading}
                  form={form}
                  name="policyType"
                  label={t("bcr.importToGos.policyType.label")}
                  options={policyTypeOptions}
                  required={true}
                  disabled={false}
                  visible={true}
                />
              </div>
              {isGeneralBusiness ? (
                <>
                  <div>
                    <label style={{ fontWeight: 600, marginBottom: 4, display: "block" }}>
                      {t("bcr.costCenterCodeId.label")}*
                    </label>
                    <SelectField
                      isLoading={isLoading}
                      form={form}
                      name="costCenterCodeId"
                      label={t("bcr.costCenterCodeId.label")}
                      options={costCenterOptions}
                      required={true}
                      disabled={false}
                      visible={true}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, marginBottom: 4, display: "block" }}>
                      {t("bcr.acquisitionAreaCodeId.label")}*
                    </label>
                    <SelectField
                      isLoading={isLoading}
                      form={form}
                      name="acquisitionAreaCodeId"
                      label={t("bcr.acquisitionAreaCodeId.label")}
                      options={acquisitionAreaOptions}
                      required={true}
                      disabled={false}
                      visible={true}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={isLoading}
              onClick={closeDialog}
            >
              {t("bcr.close.label")}
            </Button>
            <Button
              appearance="primary"
              disabled={isLoading}
              icon={
                isLoading ? (
                  <Spinner size="tiny" />
                ) : (
                  <CloudArrowUpFilled fontSize={16} />
                )
              }
              onClick={handleSubmit}
            >
              {t("bcr.importToGos.submit")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
